import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { User as UserIcon } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import { getZoneVisitTemplates, listZoneVisits, type VisitSummaryDto } from '@/api/visits';
import { formatPct, pctColorClass } from '@/components/visits/scoreDisplay';
import { NoDataForPeriod, isoDay } from '@/components/common/NoDataForPeriod';

/**
 * Статистика → Визиты: the full visit history for the zone over the
 * selected period, one row per visit — Alexey: "колонки — только группы
 * параметров и общий итог". Columns come from the zone's attached
 * template(s) (group name, in template order), not from any single visit,
 * so a template edit mid-period doesn't reshuffle old rows into new
 * columns — every row just reads its own group_id out of `groups`. A zone
 * can have more than one template attached; each row's own visit only
 * answered ONE of them, so most rows will show "—" for groups outside
 * their own template — that's expected, not a bug.
 */
export default function AnalyticsVisitsTab({
  zoneId,
  from,
  to,
}: {
  zoneId: string;
  from: string;
  to: string;
}) {
  const { t, lang } = useT();
  const locale = lang === 'en' ? 'en-US' : 'ru-RU';
  const navigate = useNavigate();

  const { data: templatesData } = useSWR(
    zoneId ? ['analytics:visits-templates', zoneId] : null,
    () => getZoneVisitTemplates(zoneId).then(r => r.data),
    { revalidateOnFocus: false },
  );
  const templates = templatesData?.results ?? [];
  // group.id -> owning template, so multi-template zones can prefix the
  // column header with the template name (single-template zones keep the
  // plain group name, unchanged from before).
  const columns = templates.flatMap(tpl => tpl.groups.map(g => ({ group: g, template: tpl })));

  const { data, isLoading } = useSWR(
    zoneId ? ['analytics:visits', zoneId, from, to] : null,
    () => {
      const fromIso = new Date(`${from}T00:00:00`).toISOString();
      const toIso = new Date(`${to}T23:59:59`).toISOString();
      return listZoneVisits(zoneId, { from: fromIso, to: toIso }).then(r => r.data);
    },
    { revalidateOnFocus: false },
  );

  const visits: VisitSummaryDto[] = (data?.results ?? []).filter(v => !v.is_draft);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  if (visits.length === 0) {
    // Тот же пустой стейт, что в отзывах и речи: визиты редкие, и короткий период
    // почти всегда пуст — предлагаем сразу перейти к разумному (Алексей 16.09).
    return <NoDataForPeriod section="visits" what="визитов" suggestion={{ label: 'Посмотреть за последние 3 месяца', from: isoDay(-90), to: isoDay(0) }} />;
  }

  // Таблица в карточке, как на соседних вкладках (Алексей 16.09, п. 51):
  // голая таблица на фоне страницы выбивалась из ряда.
  return (
    <Card variant="section">
      <CardContent className="overflow-x-auto py-4">
      <table className="w-full min-w-[36rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
            <th className="py-2 pr-3 font-medium">{t('visits.statsColTime')}</th>
            <th className="py-2 pr-3 font-medium">{t('visits.statsColAuthor')}</th>
            {columns.map(({ group, template }) => (
              <th key={group.id} className="py-2 pr-3 text-right font-medium">
                {templates.length > 1 ? `${template.name} · ${group.name}` : group.name}
              </th>
            ))}
            <th className="py-2 pl-3 text-right font-medium">{t('visits.statsColTotal')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {visits.map(v => {
            const time = new Date(v.visit_time).toLocaleString(locale, {
              day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
            });
            return (
              <tr
                key={v.id}
                onClick={() => navigate(`/usage/visits/${v.id}`)}
                className="cursor-pointer hover:bg-muted/50"
              >
                <td className="py-2 pr-3 tabular-nums text-muted-foreground">{time}</td>
                <td className="py-2 pr-3">
                  <span className="flex items-center gap-1.5">
                    <UserIcon className="size-3.5 shrink-0 text-muted-foreground" />
                    {v.author.name}
                  </span>
                </td>
                {columns.map(({ group }) => {
                  const pct = v.groups[group.id] ?? null;
                  return (
                    <td key={group.id} className={cn('py-2 pr-3 text-right tabular-nums font-medium', pctColorClass(pct, group.formula_coefficient < 0))}>
                      {formatPct(pct)}
                    </td>
                  );
                })}
                <td className={cn('py-2 pl-3 text-right tabular-nums font-semibold', pctColorClass(v.total))}>
                  {formatPct(v.total)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </CardContent>
    </Card>
  );
}
